<script>
  import { onMount } from "svelte";
  import Modal from "@bitcashier/shared/ui/Modal.svelte";
  import Loader from "../ui/Loader.svelte";
  import { t } from "../i18n/i18n.svelte.js";
  import {
    DEFAULT_USER_SETTINGS,
    NOTIFICATION_SETTINGS,
    fetchUserSettings,
    saveUserSettings,
  } from "../userSettings.js";
  import { assets, fiatAssetCodes, loadAssets } from "../assets.svelte.js";
  import { balanceSnapshot, setValueAsset } from "../balanceSnapshot.svelte.js";
  import { valueAssetOptions } from "../core/balances.js";

  // Port of src/user/modules/forms/settings/settings_forms_modal.tsx — the two-tab dialog behind
  // the header's gear icon, with Notifications as the default tab exactly as React had it.

  let { onClose } = $props();

  let tab = $state("notifications");

  // --- Notifications tab -------------------------------------------------------------------
  let settings = $state({ ...DEFAULT_USER_SETTINGS });
  let loading = $state(true);
  let loadError = $state(null);

  // --- Estimated-value tab -----------------------------------------------------------------
  // Seeded from the shared snapshot store, so the dialog opens on whatever the balances screen is
  // currently showing.
  let chosenAsset = $state(balanceSnapshot.valueAsset ?? "");

  // React showed a success snackbar; with no toast layer the confirmation (and any failure) is
  // rendered inline in the dialog instead.
  let saving = $state(false);
  let saved = $state(false);
  let saveError = $state(null);

  // React's fixed EUR/USD/GBP/PLN/JPY, widened with any configured fiat asset and the current
  // selection — see core/balances.js. Deriving this from the configured list alone dropped PLN and
  // JPY, which most tenants do not configure but the estimates endpoint accepts.
  const assetOptions = $derived(valueAssetOptions(fiatAssetCodes(), chosenAsset));

  function selectTab(next) {
    tab = next;
    saved = false;
    saveError = null;
  }

  async function save() {
    saving = true;
    saved = false;
    saveError = null;
    try {
      if (tab === "notifications") await saveUserSettings(settings);
      else await setValueAsset(chosenAsset);
      saved = true;
    } catch (error) {
      saveError = error.message || String(error);
    } finally {
      saving = false;
    }
  }

  onMount(async () => {
    loadAssets();
    try {
      settings = await fetchUserSettings();
    } catch (error) {
      loadError = error.message || String(error);
    } finally {
      loading = false;
    }
  });
</script>

<Modal title={t("settings")} {onClose}>
  <div class="admin-tabs">
    <button
      class="admin-tab"
      class:active={tab === "notifications"}
      onclick={() => selectTab("notifications")}>{t("notifications")}</button
    >
    <button
      class="admin-tab"
      class:active={tab === "estimated-value"}
      onclick={() => selectTab("estimated-value")}>{t("estimated_values_assets")}</button
    >
  </div>

  {#if tab === "notifications"}
    {#if loading}
      <Loader />
    {:else if loadError}
      <div class="error-box">{loadError}</div>
    {:else}
      <p class="section-title">Email notifications:</p>
      <div class="checks">
        {#each NOTIFICATION_SETTINGS as setting (setting.key)}
          <label class="check">
            <input type="checkbox" bind:checked={settings[setting.key]} />
            <span>{t(setting.labelKey)}</span>
          </label>
        {/each}
      </div>
    {/if}
  {:else}
    <p class="section-title">{t("estimated_values_assets")}</p>
    {#if assets.loading && !assets.loaded}
      <Loader />
    {:else}
      <select bind:value={chosenAsset} disabled={!assetOptions.length}>
        {#each assetOptions as option (option)}
          <option value={option}>{option}</option>
        {/each}
      </select>
      {#if assets.error}
        <p class="note">{t("error_requesting_data")}</p>
      {/if}
    {/if}
  {/if}

  {#if saved}
    <p class="saved">{t("updated_user_settings")}</p>
  {:else if saveError}
    <div class="error-box">{saveError}</div>
  {/if}

  {#snippet footer()}
    <button class="btn secondary" onclick={onClose}>{t("close")}</button>
    <button class="btn" disabled={saving || (tab === "notifications" && (loading || !!loadError))}
      onclick={save}>{t("save")}</button
    >
  {/snippet}
</Modal>

<style>
  .section-title {
    font-weight: 600;
    font-size: 14px;
    margin: 0 0 10px;
  }
  .checks {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
  }
  .check input {
    accent-color: var(--accent);
    width: 15px;
    height: 15px;
  }
  .note {
    color: var(--muted);
    font-size: 12px;
    margin: 10px 0 0;
  }
  .saved {
    margin: 16px 0 0;
    color: #10d876;
    font-size: 13px;
    font-weight: 600;
  }
</style>
